import { Box, CircularProgress, Link as MuiLink } from '@mui/material';
import React, { useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';

import { InteractionDialog } from '../InteractionDialog';
import { AddNotePanel } from './panels/AddNotePanel';
import { CommonSolutionsPanel } from './panels/CommonSolutionsPanel';
import { EquipmentFaultPanel } from './panels/EquipmentFaultPanel';
import { HistoryNotesPanel } from './panels/HistoryNotesPanel';
import { AllocationDialog } from './panels/InteractionPanel/AllocationDialog';
import { CheckInDialog } from './panels/InteractionPanel/CheckInDialog';
import { PartActionDialog } from './panels/InteractionPanel/PartActionDialog';
import { ReopenJobDialog } from './panels/InteractionPanel/ReopenJobDialog';
import { StatusChangeDialog } from './panels/InteractionPanel/StatusChangeDialog';
import { TaskDialog } from './panels/InteractionPanel/TaskDialog';
import { PreviousJobsPanel } from './panels/PreviousJobsPanel';
import { RepairJobDetailsPanel } from './panels/RepairJobDetailsPanel';
import { useJobTabTitle } from './useJobTabTitle';
import { useRepairJobPart } from './useRepairJobPart';
import { useServiceDetailData } from './useServiceDetailData';
import { useServiceDialogs } from './useServiceDialogs';
import { useServiceTags } from './useServiceTags';
import { ActionPermissions } from '../../../@types';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { SummaryBar } from '../../../components/molecules/SummaryBar';
import { AttachmentsPanel } from '../../../components/organisms/AttachmentsPanel';
import { LinkJobButton } from '../../../components/organisms/LinkJobButton';
import { isUserHasPermission } from '../../../configs/permissions';
import { useIsClient } from '../../../hooks/useIsClient';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { getDateFromTimestamp } from '../../../utils/common';
import { checkServiceCancelOrFutileAvailability } from '../../../utils/service-status';
import { NotFoundError } from '../../Error/NotFound';

export const RepairServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const serviceId = id ? parseInt(id, 10) : 0;

  const permissions = useGetUserSystemPermissions().data;
  // Client/customer users must not see the allocated technician (primary
  // allocation) on a repair job. Fail-closed (treats loading/error as
  // "is client") so allocation is never briefly exposed to a real client.
  const isClient = useIsClient();
  const {
    service,
    serviceQuery,
    logs,
    clientLogoPic,
    otherJobsAtLocation,
    canEditReferences,
    canEditReportedInfo,
    canEditReportedSerial,
    reportedModelOptions,
    reportedFaultOptions,
    handleAddNote,
    handleReportedInfoUpdate,
    handleSerialPick,
    handleRefresh,
    attendanceMetrics,
  } = useServiceDetailData(serviceId);

  const statusCode = service?.serviceStatus?.code ?? '';
  const serviceTypeCode = service?.serviceTypeCode ?? '';
  const dialogs = useServiceDialogs(
    serviceId,
    statusCode,
    serviceTypeCode,
    service?.mainLocationNodeId ?? 0,
    handleRefresh
  );
  const { allTags, onSaveTags } = useServiceTags(serviceId, permissions, handleRefresh);
  const serviceTypeName = service?.serviceType?.[0]?.name ?? serviceTypeCode;

  const { partSerial, partTypeName, partLocation, handlePartLocationChange } = useRepairJobPart(
    service?.reportedSerialNumberPartId
  );

  // A repair job's location is only wherever the part happens to be sitting,
  // so the tab names the part instead — same looked-up-over-reported
  // precedence the summary bar title uses.
  useJobTabTitle('Repair', id, partSerial || service?.reportedSerialNumber);

  const canEditPartLocation = isUserHasPermission(ActionPermissions.Structure_Edit, permissions);
  const canChangeStatus = isUserHasPermission(
    ActionPermissions.Service_Edit_Status_Progress_Status_Change,
    permissions
  );
  const canAllocate = isUserHasPermission(
    ActionPermissions.Service_Edit_Allocation_Primary,
    permissions
  );
  const canPartAction = isUserHasPermission(
    ActionPermissions.Service_Edit_Part_Actions,
    permissions
  );
  const canCreateTasks = isUserHasPermission(
    ActionPermissions.Service_Edit_Actions_Tasks,
    permissions
  );
  const canReopen = isUserHasPermission(
    ActionPermissions.Service_Edit_Status_Progress_Reopen_Service,
    permissions
  );
  const isClosed = Boolean(service?.serviceStatus?.isClosed);
  const summaryTitleOverride = [
    partSerial || service?.reportedSerialNumber,
    partTypeName || service?.reportedModel,
  ]
    .filter(Boolean)
    .join(': ');

  const previousJobs = useMemo(
    () =>
      otherJobsAtLocation
        .filter((j) => Number(j.id ?? j.serviceId) !== serviceId)
        .map((j) => ({
          id: Number(j.id ?? j.serviceId ?? 0),
          serviceStatus: String(j.serviceStatus ?? j.serviceStatusName ?? ''),
          createdAt: String(j.createdAt ?? ''),
          solutionAction: String(j.solutionAction ?? ''),
        })),
    [otherJobsAtLocation, serviceId]
  );

  const commonSolutions = useMemo(
    () => [] as Array<{ solutionAction: string; count: number; percentage: number }>,
    []
  );

  if (serviceQuery.isLoading) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (serviceQuery.isError || (!serviceQuery.isLoading && !service)) {
    return (
      <PageContainer>
        <NotFoundError title={id ? `Job ${id} not found` : 'Job not found'} message="" />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            fontSize: '13px',
            color: 'text.secondary',
            display: 'flex',
            gap: 0.75,
          }}
        >
          <MuiLink
            component={RouterLink}
            to="/services"
            underline="none"
            sx={{ color: 'v4.accent' }}
          >
            Jobs
          </MuiLink>
          <span>/</span>
          <span>{serviceTypeName}</span>
          <span>/</span>
          <span>JOB-{id}</span>
        </Box>

        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 50,
            bgcolor: 'background.default',
            px: 2.5,
            py: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <SummaryBar
            service={service}
            logoPic={clientLogoPic}
            hideAllocation={isClient}
            titleOverride={summaryTitleOverride || undefined}
            allTags={allTags}
            onSaveTags={onSaveTags}
            onStatusChange={canChangeStatus ? dialogs.handleOpenStatusDialog : undefined}
            onAllocate={canAllocate ? dialogs.handleOpenAllocationDialog : undefined}
            onPartAction={canPartAction ? dialogs.handleOpenPartActionDialog : undefined}
            onTask={canCreateTasks ? dialogs.handleOpenTaskDialog : undefined}
            onCancelFutile={
              checkServiceCancelOrFutileAvailability(permissions || [], false, statusCode) &&
              !isClosed
                ? dialogs.handleOpenCancelFutileDialog
                : undefined
            }
            onReopen={canReopen && isClosed ? dialogs.handleOpenReopenDialog : undefined}
          />
        </Box>

        <StatusChangeDialog
          open={dialogs.statusDialogOpen}
          onClose={dialogs.handleCloseStatusDialog}
          currentStatus={service?.serviceStatus?.name ?? ''}
          possibleStatuses={dialogs.nextStatusesQuery.data?.possibleNextStatuses ?? []}
          preferredStatuses={dialogs.nextStatusesQuery.data?.preferedStatuses ?? []}
          loading={dialogs.nextStatusesQuery.isFetching}
          onSubmit={dialogs.handleStatusSubmit}
          submitting={dialogs.statusSubmitting}
        />
        <AllocationDialog
          open={dialogs.allocationDialogOpen}
          onClose={dialogs.handleCloseAllocationDialog}
          currentTechnician={service?.primaryAllocation?.node?.name ?? ''}
          onSubmit={dialogs.handleAllocationSubmit}
          submitting={dialogs.allocationSubmitting}
        />
        <PartActionDialog
          open={dialogs.partActionDialogOpen}
          onClose={dialogs.handleClosePartActionDialog}
          serviceId={serviceId}
          clientId={service?.clientId ?? 0}
          contractId={service?.contractId ?? 0}
          locationId={service?.mainLocationNodeId ?? 0}
          serviceTypeCode={service?.serviceTypeCode ?? ''}
          reportedSerialNumberPartId={service?.reportedSerialNumberPartId || undefined}
          canCreateExceptional={isUserHasPermission(
            ActionPermissions.Service_Edit_Part_Actions_Exceptional_Creation,
            permissions
          )}
          onSubmit={dialogs.handlePartActionSubmit}
          submitting={dialogs.partActionSubmitting}
        />
        <TaskDialog
          open={dialogs.taskDialogOpen}
          onClose={dialogs.handleCloseTaskDialog}
          serviceId={serviceId}
          taskTypes={dialogs.taskTypesQuery.data ?? []}
          taskTypesLoading={dialogs.taskTypesQuery.isFetching}
          onSubmit={dialogs.handleTaskSubmit}
          submitting={dialogs.taskSubmitting}
        />
        <StatusChangeDialog
          open={dialogs.cancelFutileDialogOpen}
          onClose={dialogs.handleCloseCancelFutileDialog}
          currentStatus={service?.serviceStatus?.name ?? ''}
          possibleStatuses={dialogs.cancelFutileStatusesQuery.data ?? []}
          preferredStatuses={[]}
          loading={dialogs.cancelFutileStatusesQuery.isFetching}
          onSubmit={dialogs.handleCancelFutileSubmit}
          submitting={dialogs.statusSubmitting}
        />
        <ReopenJobDialog
          open={dialogs.reopenDialogOpen}
          onClose={dialogs.handleCloseReopenDialog}
          currentStatus={service?.serviceStatus?.name ?? ''}
          reopenStatuses={dialogs.reopenStatusesQuery.data ?? []}
          loading={dialogs.reopenStatusesQuery.isFetching}
          onSubmit={dialogs.handleReopenSubmit}
          submitting={dialogs.statusSubmitting}
        />
        <CheckInDialog
          open={dialogs.checkInDialogOpen}
          onClose={dialogs.handleCloseCheckInDialog}
          onSubmit={dialogs.handleCheckInSubmit}
          submitting={dialogs.checkInSubmitting}
          travelStartTime={
            service?.travelStartTime ? getDateFromTimestamp(service.travelStartTime) : ''
          }
        />
        <InteractionDialog
          interactions={dialogs.pendingInteractions}
          permissions={permissions}
          onComplete={dialogs.clearPendingInteractions}
          onCancel={dialogs.cancelPendingInteractions}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 3fr',
            gap: 1.75,
            px: 2.5,
            pt: 1.75,
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <RepairJobDetailsPanel
              service={service}
              isClient={isClient}
              partSerial={partSerial}
              partTypeName={partTypeName}
              partLocation={partLocation}
              canEditLocation={canEditPartLocation}
              onPartLocationChange={handlePartLocationChange}
              headerAction={<LinkJobButton serviceId={serviceId} permissions={permissions} />}
            />
            <AttachmentsPanel entityId={serviceId} />
            <AddNotePanel onAddNote={handleAddNote} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <EquipmentFaultPanel
              service={service}
              canEditReferences={canEditReferences}
              canEditReportedInfo={canEditReportedInfo}
              canEditReportedSerial={canEditReportedSerial}
              reportedModelOptions={reportedModelOptions}
              reportedFaultOptions={reportedFaultOptions}
              onReportedInfoUpdate={handleReportedInfoUpdate}
              onSerialPick={handleSerialPick}
            />
            <PreviousJobsPanel jobs={previousJobs} serialNumber={service?.reportedSerialNumber} />
            <CommonSolutionsPanel
              solutions={commonSolutions}
              partTypeName={service?.reportedModel}
            />
          </Box>
        </Box>

        <Box sx={{ px: 2.5, py: 1.75 }}>
          <HistoryNotesPanel
            logs={logs}
            serviceId={serviceId}
            permissions={permissions}
            highlightPartActions
            attendanceMetrics={attendanceMetrics}
          />
        </Box>
      </Box>
    </PageContainer>
  );
};
